import React from 'react';

import { useIntl } from '@edx/frontend-platform/i18n';
import {
  Badge, Card, Icon, Truncate,
} from '@openedx/paragon';
import { ExpandLess, ExpandMore } from '@openedx/paragon/icons';

import { reduxHooks } from 'hooks';
import AccessSourceBadge from 'components/Badges/AccessSourceBadge';
import StructuredCourseSections from 'components/StructuredCourseSections';
import { structuredCourseView } from 'data/redux/app/selectors/navSelectors';

import { useProductAccordion } from './hooks';
import messages from './messages';
import './index.scss';

/**
 * Horizontal accordion: one row per product in the currently selected
 * product category. Clicking a row expands its structured course list
 * inline, below the row, without navigating away from the product list —
 * so switching between products (or seeing them all again) never requires
 * a trip back through the nav bar. Expired products are shown (dimmed) but
 * are not expandable — access to their courses has lapsed.
 */
export const ProductCardGrid = () => {
  const { formatMessage } = useIntl();
  const products = reduxHooks.useSelectedProductCategoryCards();
  const { expandedIndex, toggle } = useProductAccordion();

  return (
    <div className="product-accordion">
      {products.map((product, index) => {
        const title = product.product_name || formatMessage(messages.genericProductName);
        const isExpired = !!product.is_expired;
        const isExpanded = !isExpired && expandedIndex === index;
        const toggleLabel = formatMessage(
          isExpanded ? messages.collapseProduct : messages.expandProduct,
          { title },
        );
        const handleToggle = () => {
          if (isExpired) { return; }
          toggle(index);
        };
        return (
          // eslint-disable-next-line react/no-array-index-key
          <div key={`${product.product_name || 'product'}-${index}`} className="product-accordion-item">
            <Card
              className={`product-card ${isExpired ? 'product-card-expired' : ''}`}
              orientation="horizontal"
              tabIndex={isExpired ? -1 : 0}
              role={isExpired ? undefined : 'button'}
              aria-expanded={isExpired ? undefined : isExpanded}
              aria-disabled={isExpired || undefined}
              aria-label={isExpired ? undefined : toggleLabel}
              onClick={isExpired ? undefined : handleToggle}
              onKeyPress={isExpired ? undefined : handleToggle}
              isClickable={!isExpired}
            >
              {product.product_image && (
                <Card.ImageCap
                  src={product.product_image}
                  srcAlt={formatMessage(messages.productImageAlt, { title })}
                />
              )}
              <Card.Body>
                <Card.Header
                  title={(
                    <Truncate lines={2} className="product-card-title">
                      {title}
                    </Truncate>
                  )}
                />
                <Card.Section>
                  <div className="d-flex flex-wrap align-items-center product-card-badges">
                    {isExpired && (
                      <Badge variant="danger" className="mr-1">
                        {product.expiry_date
                          ? formatMessage(messages.expiredOn, { expiryDate: product.expiry_date })
                          : formatMessage(messages.expired)}
                      </Badge>
                    )}
                    {!isExpired && product.expiry_date && (
                      <Badge variant="light" className="mr-1">
                        {formatMessage(messages.expiresOn, { expiryDate: product.expiry_date })}
                      </Badge>
                    )}
                    <AccessSourceBadge accessSource={product.access_source} />
                  </div>
                </Card.Section>
              </Card.Body>
              {!isExpired && (
                <Icon
                  src={isExpanded ? ExpandLess : ExpandMore}
                  className="product-card-chevron"
                />
              )}
            </Card>
            {isExpanded && (
              <div className="product-accordion-panel">
                <StructuredCourseSections
                  subCategories={structuredCourseView(product.sub_categories)}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ProductCardGrid;
